import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';

import { PartnerRecordDto, PartnersApiService } from '@api-open';
import { ThemeColorService } from '@tk/shared/lib';

import { NavigationPalette, NavigationLogos, NavigationCustomization } from './navigation-customization.interface';
export const defaultColors = {
  bg: '#22154D',
  itemText: '#FFFFFFFF',
  itemBg: '#FFFFFF00',
  itemBgHover: '#7D58F74D',
  itemTextActive: '#FFFFFFFF',
  itemBgActive: '#7D58F74D',
  itemBgActiveHover: '#7D58F7B2',
  bgCollapsedSubItem: '#22154DFF',
} as const;

@Injectable({
  providedIn: 'root',
})
export class CustomizationService {
  readonly partnerInfo = (() => {
    const partnersApiService = inject(PartnersApiService);
    const domain = inject(DOCUMENT).location.host;
    return rxResource({
      loader: () => partnersApiService.partnerInfo({ domain }),
    });
  })();
  readonly navCustomization = computed(() => {
    if (this.partnerInfo.hasValue()) {
      return this.getNavCustomization(this.partnerInfo.value());
    }
  });

  readonly logo = computed(() => {
    if (this.partnerInfo.hasValue()) {
      const logoUrl = this.partnerInfo.value()?.logoUrl;
      if (logoUrl) {
        return logoUrl;
      }
      return this.taikunLogo();
    }
  });

  readonly taikunLogo = computed(() =>
    this.themeColorService.renderedTheme() === 'dark'
      ? 'assets/taikun-logo2-dark.svg'
      : 'assets/taikun-logo2-light.svg',
  );

  private readonly themeColorService = inject(ThemeColorService);

  private getNavCustomization(partnerInfo: PartnerRecordDto): NavigationCustomization {
    return {
      palette: this.getNavPalette(partnerInfo),
      logo: this.getNavLogos(partnerInfo),
    };
  }

  private getNavPalette(_partnerInfo: PartnerRecordDto): NavigationPalette {
    const {
      bg = '',
      bgCollapsedSubItem = '',
      itemText = '',
      itemBg = '',
      itemBgHover = '',
      itemTextActive = '',
      itemBgActive = '',
      itemBgActiveHover = '',
    } = _partnerInfo.partnerColorSettings ?? {};

    return {
      // TODO: When strictNullChecks is enabled, replace non-null assertions with proper null checks
      bg: bg === defaultColors.bg ? 'linear-gradient( 180deg, #22154D 0%, #37227B 100%)' : bg!,
      bgCollapsedSubItem: bgCollapsedSubItem!,
      itemText: itemText!,
      itemBg: itemBg!,
      itemBgHover: itemBgHover!,
      itemTextActive: itemTextActive!,
      itemBgActive: itemBgActive!,
      itemBgActiveHover: itemBgActiveHover!,
    };
  }

  private getNavLogos(_partnerInfo: PartnerRecordDto): NavigationLogos {
    const { collapsed, expanded } = _partnerInfo.partnerImageSettings ?? {};

    return {
      // TODO: When strictNullChecks is enabled, replace non-null assertions with proper null checks
      collapsed: collapsed!,
      expanded: expanded!,
    };
  }
}
